import { SignatureWithSpotgrid } from '../../types/types';

export const OBJECTIVE_COMPARISON_SIMILAR = 'SIMILAR';
export const OBJECTIVE_COMPARISON_DIFFERENT = 'DIFFERENT';
export const INTERPRETED_COMPARISON_PASS = 'PASS';
export const INTERPRETED_COMPARISON_FAIL = 'FAIL';

/*
N - number of records
M - number of spots
*/

export type signal = number[][]; // NxM matrix
export type signature = number[]; // 1xM matrix

// ------ Processing parametrisation ------
// ----------------------------------------
export interface ProcessingPipelineParams {
  pipelineType: PipelineType;
  aggregation?: AggregationMethod;
  normalisation?: NormalisationMethod;
  humidityCorrectionParams?: HumidityCorrectionParam;
}

export enum PipelineType {
  Classic = 'Classic', // 1. senso -> signature ; 2. aggregate on sensograms ; 3. normalisation
  DOH = 'DOH', // 1. aggregate on sensograms ; 2. senso -> signature ; 3. normalisation
  Slope = 'Slope', // compute signal slopes
}

export interface HumidityCorrectionParam {
  deltaH: number;
  calibrant: SignatureWithSpotgrid;
}

export enum AggregationMethod {
  Mean = 'Mean',
  Median = 'Median',
}

export type NormalisationMethod = BasicNormalisation | CustomMinMaxNormalisation;

export enum BasicNormalisationMethod {
  L2 = 'L2',
}

export interface BasicNormalisation {
  type: 'basic';
  method: BasicNormalisationMethod;
}

export interface CustomMinMaxNormalisation {
  type: 'customMinMax';
  rules: CustomMinMaxNormalisationRules;
}

// define the types for custom min max normalisation
export interface CustomMinMaxNormalisationRules {
  minSpot: number;
  maxSpot: number;
}

// Define interfaces for the YAML structure
export interface Rule {
  condition: string;
  result: string;
}
export interface ClassificationRules {
  rules: Rule[];
  thresholds?: Threshold;
  waterCalibrant?: WaterCalibrant;
  default: string;
}

// ------ Interfaces to store various processing results ------
// ------------------------------------------------------------
export interface Envelope {
  avg: number;
  min: number;
  max: number;
}

export interface LinearFitResult {
  a: number; // Slope
  b: number; // Intercept
  rSquared: number; // R² value
}

export interface OdorDetectionInfos {
  isOdorPresent: boolean;
  noizeLevel: number;
  thresholdLevel: number;
  maxIntensity: number;
  startTimestamp: number;
  stopTimestamp: number;
  lastRecognitionTimestamp: number;
}

/*
Full ellipse definition. To be used in the App
*/
export interface ellipse {
  group: string;
  cx: number; // Center, x
  cy: number; // Center, y
  a: number; // Semi-major axis
  b: number; // Semi-minor axis
  theta: number; // Counter-clockwise angle of rotation in radians (between the x axis and the semi-major axis)
}

/*
Simplified version of ellipse. Already calculated, translated and rotated focal points (x,y)
To be transmitted to the watch
*/
export interface simplifiedEllipse {
  group: string;
  p: number; // Perimeter: 2*a
  focusAx: number; // Coordinates of the right focus of an ellipse, x
  focusAy: number; // Coordinates of the right focus of an ellipse, y
  focusBx: number; // Coordinates of the left focus of an ellipse, x
  focusBy: number; // Coordinates of the left focus of an ellipse, y
}

export interface Threshold {
  rawMziDetectionThreshold: number;
  distanceToWaterCalibrantThreshold: number;
  humidityCorrectedMziExploitableThreshold: number;
}

export interface WaterCalibrant {
  id: string;
  timestamp: number;
  waterAffinities: number[];
  spotgrid: number[];
  rawSignature: number[];
}

// Define the types for barycenters
export interface Barycenters {
  [key: string]: [number, number];
}

// ------ Generic Model Related ------
// -----------------------------------
export type Model = GenericModel | GenericModelV2;

// define enum
export enum ModelCategory {
  Barycenters = 'Barycenters',
  ComparisonSignatures = 'ComparisonSignatures',
  ComparisonIntensities = 'ComparisonIntensities',
  ChemicalPrediction = 'ChemicalPrediction',
  RulesBased = 'RulesBased',
  Ellipses = 'Ellipses',
}

// For Generic Models
// Define the main data structure
// TODO soon be deprecated
export interface GenericModel {
  metadata: Metadata;
  projection_axis?: number[][];
  barycenters?: Barycenters;
  referencesSignatures?: number[][];
  referenceIntensities?: number[];
  comparisonThreshold?: number;
  analyteDuration?: number;
  chemicalRules?: ClassificationRules;
  normalisationForChemicalClassification?: CustomMinMaxNormalisationRules;
  barycentersAsSignatures?: Record<string, number[]>[];
  classToStimulationDictionnary?: ClassToStimulationDictionary;
}

export type ModelData = ComparisonSignatureData | ComparisonIntensitiesData | BarycentersData | EllipsesData | ChemicalPredictionData | RulesBasedData;

export interface GenericModelV2 {
  metadata: Metadata;
  data: ModelData;
}

// Define the types for metadata
export interface Metadata {
  created_at: string;
  spotfile: number[];
  ID: string;
  type?: ModelCategory;
  debug?: boolean;
}

export interface ComparisonSignatureData {
  type: ModelCategory.ComparisonSignatures;

  // parameters
  comparisonThreshold: number;

  // computed data
  referencesSignatures: number[][];
}

export interface ComparisonIntensitiesData {
  type: ModelCategory.ComparisonIntensities;

  // parameters
  comparisonThreshold: number;

  // computed data
  referencesIntensities: number[];
}

export interface BarycentersData {
  type: ModelCategory.Barycenters;

  // parameters

  // computed data
  barycenters: Barycenters;
  projectionAxis: number[][];
}

export interface EllipsesData {
  type: ModelCategory.Ellipses;

  // parameters
  sigmaX: number;
  sigmaY: number;

  // computed data
  groupedScaledEllipses: Record<string, number[]>;
  pcaEigenvectors: number[][];
  cmap: Record<string, [number, number, number]>;
}

export interface ChemicalPredictionData {
  type: ModelCategory.ChemicalPrediction;

  // params
  chemicalRules: ClassificationRules;
  customNormalisation?: CustomMinMaxNormalisationRules;
  classToStimulationDictionnary: ClassToStimulationDictionary;

  // computed
  barycentersAsSignatures?: Record<string, number[]>[];
  barycenters: Barycenters;
  projection_axis?: number[][];
}

export interface RulesBasedData {
  type: ModelCategory.RulesBased;

  // params
  customProcessingParams?: ProcessingPipelineParams;
  rules: ClassificationRules;
}

export interface ClassToStimulationDictionary {
  amine: Stimulation;
  humid_air: Stimulation;
  ketone: Stimulation;
  alcohol: Stimulation;
  acid: Stimulation;
  terpene: Stimulation;
  phenolic: Stimulation;
}

export interface Stimulation {
  frequency: number;
  duration: number;
}
